import "./global.css";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { SidebarProvider } from "@/context/SidebarContext";
import { HandoutProvider } from "@/context/HandoutContext";
import { OrganizationProvider } from "@/context/OrganizationContext";
import { ProficiencyLevelsProvider } from "@/context/ProficiencyLevelsContext";
import { DemoProvider } from "@/contexts/DemoContext";
import { lazy, Suspense, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import { supabase } from "@/integrations/supabase/client";

// ─── Lazy-loaded pages ───────────────────────────────────────────────
const Index = lazy(() => import("./pages/Index"));
const Competencies = lazy(() => import("./pages/Competencies"));
const AdminCompetencies = lazy(() => import("./pages/AdminCompetencies"));
const MeetingPlan = lazy(() => import("./pages/MeetingPlan"));
const MeetingPlanGenerated = lazy(() => import("./pages/MeetingPlanGenerated"));
const PrepareMeeting = lazy(() => import("./pages/PrepareMeeting"));
const MeetJohn = lazy(() => import("./pages/MeetJohn"));
const EmployeeHandout = lazy(() => import("./pages/EmployeeHandout"));
const EmployeeProfile = lazy(() => import("./pages/EmployeeProfile"));
const Modeles = lazy(() => import("./pages/Modeles"));
const TemplateDetail = lazy(() => import("./pages/TemplateDetail"));
const SurveyPreview = lazy(() => import("./pages/SurveyPreview"));
const TranslationPage = lazy(() => import("./pages/TranslationPage"));
const EmployeeMeetings = lazy(() => import("./pages/EmployeeMeetings"));
const Assessment = lazy(() => import("./pages/Assessment"));
const AssessmentSurvey = lazy(() => import("./pages/AssessmentSurvey"));
const EmployeePortal = lazy(() => import("./pages/EmployeePortal"));
const Objectives = lazy(() => import("./pages/Objectives"));
const ObjectiveDetail = lazy(() => import("./pages/ObjectiveDetail"));
const CreateObjective = lazy(() => import("./pages/CreateObjective"));
const CreateObjectiveForm = lazy(() => import("./pages/CreateObjectiveForm"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Employees = lazy(() => import("./pages/Employees"));
const Login = lazy(() => import("./pages/Login"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const AuthVerify = lazy(() => import("./pages/AuthVerify"));
const TalentAnalysis = lazy(() => import("./pages/TalentAnalysis"));
const TalentCategoryDetail = lazy(() => import("./pages/TalentCategoryDetail"));
const Questionnaire = lazy(() => import("./pages/Questionnaire"));
const CompetencyProfiles = lazy(() => import("./pages/CompetencyProfiles"));
const Intro = lazy(() => import("./pages/Intro"));
const EmployeeDevelopment = lazy(() => import("./pages/EmployeeDevelopment"));
const ManagerAssessmentTemplate = lazy(() => import("./pages/ManagerAssessmentTemplate"));
const MeetingPlanTemplateEditor = lazy(() => import("./pages/MeetingPlanTemplateEditor"));
const Vision = lazy(() => import("./pages/Vision"));
const EvaluationCycles = lazy(() => import("./pages/EvaluationCycles"));
const EvaluationCycleDetail = lazy(() => import("./pages/EvaluationCycleDetail"));
const Cycles360 = lazy(() => import("./pages/Cycles360"));
const StorybookPage = lazy(() => import("./pages/Storybook"));
const AdminLogs = lazy(() => import("./pages/AdminLogs"));
const CompanyAdmin = lazy(() => import("./pages/CompanyAdmin"));
const Permissions = lazy(() => import("./pages/Permissions"));
const Evaluations360 = lazy(() => import("./pages/Evaluations360"));
const Compensation = lazy(() => import("./pages/Compensation"));
const CompensationAdmin = lazy(() => import("./pages/CompensationAdmin"));
const StaticEvaluationDemo = lazy(() => import("./pages/StaticEvaluationDemo"));
const Demo = lazy(() => import("./pages/Demo"));
const DemoV2 = lazy(() => import("./pages/DemoV2"));
const DemoV3 = lazy(() => import("./pages/DemoV3"));
const ScenarioBilan = lazy(() => import("./pages/demo-v3/ScenarioBilan"));
const Scenario1Page = lazy(() =>
  import("./pages/demo-v2/ScenarioPlaceholder").then((m) => ({ default: m.Scenario1Page })),
);
const Scenario2Page = lazy(() =>
  import("./pages/demo-v2/ScenarioPlaceholder").then((m) => ({ default: m.Scenario2Page })),
);
const Scenario3Page = lazy(() =>
  import("./pages/demo-v2/ScenarioPlaceholder").then((m) => ({ default: m.Scenario3Page })),
);

// Mentoring pages
const FindMentor = lazy(() => import("./pages/mentoring/FindMentor"));
const MySessions = lazy(() => import("./pages/mentoring/MySessions"));
const MentoringStats = lazy(() => import("./pages/mentoring/MentoringStats"));

// ─── Route-level loading fallback ────────────────────────────────────
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function SyncAuthenticatedLoginRoute() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname !== "/login") return;

    let isMounted = true;
    const redirectIfAuthenticated = (isAuthenticated: boolean) => {
      if (isMounted && isAuthenticated && window.location.pathname === "/login") {
        navigate("/", { replace: true });
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      redirectIfAuthenticated(Boolean(session));
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      redirectIfAuthenticated(Boolean(session));
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [pathname, navigate]);

  return null;
}

export default function App() {
  const [queryClient] = useState(() => new QueryClient());
  return (
    <QueryClientProvider client={queryClient}>
      <SidebarProvider>
        <OrganizationProvider>
        <ProficiencyLevelsProvider>
        <HandoutProvider>
        <DemoProvider>
          <TooltipProvider>
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <SyncAuthenticatedLoginRoute />
              <Routes>
                  {/* Public routes — no layout */}
                  <Route path="/login" element={<Suspense fallback={<PageLoader />}><Login /></Suspense>} />
                  <Route path="/auth/callback" element={<Suspense fallback={<PageLoader />}><AuthCallback /></Suspense>} />
                  <Route path="/auth/verify" element={<Suspense fallback={<PageLoader />}><AuthVerify /></Suspense>} />

                  {/* Protected routes — shared AppLayout (Header + Sidebar + margin) */}
                  {/* Suspense is handled inside AppLayout for seamless SPA transitions */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<AppLayout />}>
                      <Route path="/" element={<Intro />} />
                      <Route path="/evaluation" element={<Index />} />
                      <Route path="/evaluation/:evaluationId" element={<Index />} />
                      <Route path="/employes" element={<Employees />} />
                      <Route path="/intro" element={<Intro />} />
                      <Route path="/employee-profile" element={<EmployeeProfile />} />
                      <Route path="/employee-portal" element={<EmployeePortal />} />
                      <Route path="/employee-portal/development" element={<EmployeeDevelopment />} />
                      <Route path="/objectifs" element={<Objectives />} />
                      <Route path="/objectifs/new" element={<CreateObjective />} />
                      <Route path="/objectifs/new/:type" element={<CreateObjectiveForm />} />
                      <Route path="/objectifs/:id" element={<ObjectiveDetail />} />
                      <Route path="/rapports/competences" element={<Competencies />} />
                      <Route path="/rapports/analyse-talents" element={<TalentAnalysis />} />
                      <Route path="/rapports/analyse-talents/:id" element={<TalentCategoryDetail />} />
                      <Route path="/rencontres-employes" element={<EmployeeMeetings />} />
                      <Route path="/assessment-employee" element={<Assessment />} />
                      <Route path="/assessment-employee/survey" element={<AssessmentSurvey />} />
                      <Route path="/assessment-employee/:evaluationId" element={<Assessment />} />
                      <Route path="/assessment-employee/:evaluationId/survey" element={<AssessmentSurvey />} />
                      <Route path="/assessment-results" element={<MeetingPlan />} />
                      <Route path="/assessment-results/:evaluationId" element={<MeetingPlan />} />
                      <Route path="/meeting-plan-generated" element={<MeetingPlanGenerated />} />
                      <Route path="/prepare-meeting" element={<PrepareMeeting />} />
                      <Route path="/prepare-meeting/:evaluationId" element={<PrepareMeeting />} />
                      <Route path="/meet-employee" element={<MeetJohn />} />
                      <Route path="/meet-employee/:evaluationId" element={<MeetJohn />} />
                      <Route path="/administration/modeles" element={<Modeles />} />
                      <Route path="/administration/modeles/new" element={<TemplateDetail />} />
                      <Route path="/administration/modeles/manager-assessment/new" element={<ManagerAssessmentTemplate />} />
                     <Route path="/administration/modeles/meeting-plan/new" element={<MeetingPlanTemplateEditor />} />
                     <Route path="/administration/modeles/meeting-plan/:id" element={<MeetingPlanTemplateEditor />} />
                      <Route path="/administration/modeles/:id" element={<TemplateDetail />} />
                      <Route path="/administration/modeles/:id/translate" element={<TranslationPage />} />
                      <Route path="/administration/modeles/preview/:id" element={<SurveyPreview />} />
                      <Route path="/administration/profils-competences" element={<CompetencyProfiles />} />
                      <Route path="/administration/profils-competences/nouveau" element={<CompetencyProfiles />} />
                      <Route path="/administration/competences" element={<AdminCompetencies />} />
                      <Route path="/administration/vision" element={<Vision />} />
                      <Route path="/administration/compagnie" element={<CompanyAdmin />} />
                      <Route path="/administration/evaluation-cycles/:id" element={<EvaluationCycleDetail />} />
                      <Route path="/administration/evaluation-cycles" element={<EvaluationCycles />} />
                      <Route path="/administration/cycles-360" element={<Cycles360 />} />
                      <Route path="/administration/storybook" element={<StorybookPage />} />
                      <Route path="/administration/composants" element={<Navigate to="/administration/storybook" replace />} />
                      <Route path="/administration/composants-deux" element={<Navigate to="/administration/storybook" replace />} />
                      <Route path="/administration/logs" element={<AdminLogs />} />
                      <Route path="/administration/permissions" element={<Permissions />} />
                      <Route path="/questionnaire" element={<Questionnaire />} />
                      <Route path="/evaluations-360" element={<Evaluations360 />} />
                      <Route path="/remuneration" element={<Compensation />} />
                      <Route path="/administration/remuneration" element={<CompensationAdmin />} />
                      {/* Demo */}
                      <Route path="/demo" element={<Demo />} />
                      <Route path="/demo/:step" element={<Demo />} />
                      <Route path="/demo-v2" element={<DemoV2 />} />
                      <Route path="/demo-v2/scenario-1" element={<Scenario1Page />} />
                      <Route path="/demo-v2/scenario-2" element={<Scenario2Page />} />
                      <Route path="/demo-v2/scenario-3" element={<Scenario3Page />} />
                      <Route path="/demo-v3" element={<DemoV3 />} />
                      <Route path="/demo-v3/scenario-bilan" element={<ScenarioBilan />} />
                      <Route path="/demo-v3/:step" element={<DemoV3 />} />
                      <Route path="/demo-evaluation-statique" element={<StaticEvaluationDemo />} />
                      {/* Mentoring routes */}
                      <Route path="/mentorat/trouver" element={<FindMentor />} />
                      <Route path="/mentorat/sessions" element={<MySessions />} />
                      <Route path="/mentorat/statistiques" element={<MentoringStats />} />
                    </Route>
                  </Route>

                  {/* Standalone pages — no AppLayout */}
                  <Route path="/employee-handout" element={<Suspense fallback={<PageLoader />}><EmployeeHandout /></Suspense>} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
            </BrowserRouter>
          </TooltipProvider>
        </DemoProvider>
        </HandoutProvider>
        </ProficiencyLevelsProvider>
        </OrganizationProvider>
      </SidebarProvider>
    </QueryClientProvider>
  );
}
